import React, {useState} from 'react';

import RemoteIconV2 from '@/components/Icon/RemoteIconV2';
import Image from '@/components/Image/Image';
import {getImageByViewType, IMAGE_VIEW_TYPES} from '@/consts/icc/imageViewTypes';
import appContent from '@/services/content/appContent.typed';
import type {IconModel} from '@/types/aem';
import type {AutoDeliveryImage, AutoDeliveryPrice, AutoDeliveryProduct} from '@/types/icc/autoDeliveryDetails';
import {formatString} from '@/utils/stringUtils';

import AutoDeliveryDcxOrderHeader from './AutoDeliveryDcxOrderHeader';
import AutoDeliveryDcxOrderSummary from './AutoDeliveryDcxOrderSummary';
import AutoDeliveryDcxSection from './AutoDeliveryDcxSection';

interface AutoDeliveryDcxOrderProps {
    className?: string;
    products: AutoDeliveryProduct[] | null;
    price: AutoDeliveryPrice;
    isEditAllowed?: boolean;
    isUpdateDisabled?: boolean;
    isCompact?: boolean;
}

const AutoDeliveryDcxOrder = ({
    className,
    products,
    price,
    isEditAllowed,
    isUpdateDisabled,
    isCompact,
}: AutoDeliveryDcxOrderProps) => {
    if (!products?.length) return null;

    const currencySign = price.currencySign;

    return (
        <AutoDeliveryDcxSection className={className}>
            <AutoDeliveryDcxOrderHeader
                className='ml:mt-8'
                isEditAllowed={isEditAllowed}
                isUpdateDisabled={isUpdateDisabled}
                isCompact={isCompact}
            />
            <ul className='ml:mt-2'>
                {products.map((product, i) => (
                    <ProductListItem product={product} currencySign={currencySign} key={i} />
                ))}
            </ul>
            {price && (
                <AutoDeliveryDcxOrderSummary className='border-t border-primary-dark-15 px-0 pt-4' price={price} />
            )}
        </AutoDeliveryDcxSection>
    );
};

export default AutoDeliveryDcxOrder;

interface ProductListItemProps {
    product: AutoDeliveryProduct;
    currencySign: string | null;
}

const ProductListItem = ({product, currencySign}: ProductListItemProps) => {
    const {name, images, quantity, price, isOutOfStock} = product;
    const {yourOrderQuantityLabel, yourOrderOutOfStockIcon, productImagePlaceholderIcon} =
        appContent.getAutoDeliveryContent()?.extendedDetailsContent || {};
    const quantityLabel = yourOrderQuantityLabel ? formatString(yourOrderQuantityLabel, quantity) : null;

    return (
        <li className='flex items-center gap-2 border-b border-primary-dark-15 py-4 last:border-none md:py-2 lg:py-4'>
            <ProductImage images={images} placeholderIcon={productImagePlaceholderIcon} />
            <div className='flex-1 text-14 leading-[20px]'>
                {name && <div className='tracking-04'>{name}</div>}
                {quantityLabel && <div className='font-bold tracking-02'>{quantityLabel}</div>}
                {isOutOfStock && yourOrderOutOfStockIcon && (
                    <div className='mt-2 flex items-center justify-start gap-2'>
                        <RemoteIconV2 className='size-4' icon={yourOrderOutOfStockIcon} />
                        {yourOrderOutOfStockIcon.title && (
                            <span className='text-12 tracking-02'>{yourOrderOutOfStockIcon.title}</span>
                        )}
                    </div>
                )}
            </div>
            {price != null && (
                <div className='whitespace-nowrap text-16 font-bold leading-15'>
                    {currencySign ? `${currencySign} ${price}` : price}
                </div>
            )}
        </li>
    );
};

interface ProductImageProps {
    images?: AutoDeliveryImage[] | null;
    placeholderIcon?: IconModel;
}

const ProductImage = ({images, placeholderIcon}: ProductImageProps) => {
    const [hasError, setHasError] = useState(false);
    const {link} = getImageByViewType(images, IMAGE_VIEW_TYPES.SMALL) || {};
    const showPlaceholder = !link || hasError;

    return (
        <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-wild-sand'>
            {showPlaceholder ? (
                placeholderIcon && <RemoteIconV2 className='size-6' icon={placeholderIcon} />
            ) : (
                <Image
                    src={link}
                    className='h-full w-full rounded-md object-contain'
                    onError={() => setHasError(true)}
                />
            )}
        </div>
    );
};
